import { formatBytes, type RecordFileItem } from '@ofmos/shared/files';
import { Icon } from '../../ui';
import { fileIcon } from './fileItems';

/**
 * ONE ATTACHMENT, ON A PHONE (`docs/decisions/file-attachments.md` D18).
 *
 * THE QUESTION D18 ASKS IS "WOULD AN iOS APP ARRANGE IT THIS WAY", and the answer for
 * `FileRow`'s desktop shape is no: an iOS app does not put a preview, a name, a size and two
 * buttons on one row of a scrolling list. So the whole ROW is one tap target and it opens a
 * `BottomSheet` carrying everything the row was trying to hold.
 *
 * THREE CONSEQUENCES, each one a defect the desktop shape would have had here:
 *  - the row shows the NAME and the SIZE and nothing else. A content-type chip is chrome,
 *    and `DESIGN.md:56` says density belongs to data rows, not to chrome;
 *  - the two actions are FULL-WIDTH inside the sheet, where a thumb reaches them, rather
 *    than two `sm` buttons sharing a 32-pixel row;
 *  - the destructive confirmation lives INSIDE the sheet, because a popover anchored to a
 *    row that small is a mis-tap generator.
 *
 * THE SHEET IS NO LONGER THIS COMPONENT'S (wave 1c, finding M1). It was, and a sheet owned by
 * the row it removes cannot close: the row unmounts on the removal and takes the sheet with
 * it, so `onCloseAutoFocus` never runs and focus is left on `<body>`. The sheet belongs to
 * the FIELD now (`FileList` -> `FileSheet`); this row opens it and nothing else. D18 is
 * untouched by the move - the confirmation is still INSIDE the sheet, only the owner changed.
 *
 * `.pressable` on the row: on a phone a tap that changes nothing until the sheet animates in
 * reads as an unresponsive control, which is the rule the mechanisms checklist states outright
 * ("Always include a press state for a custom button").
 */
export interface FileSheetRowProps {
  readonly item: RecordFileItem;
  readonly onOpen: () => void;
}

export function FileSheetRow({ item, onOpen }: FileSheetRowProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="pressable flex min-h-11 w-full items-center gap-3 rounded-control px-1 py-2 text-left"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control border border-line bg-surface-sunken text-ink-muted">
        <Icon name={fileIcon(item)} size="1.1rem" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-ink">{item.label}</span>
        <span className="block text-xs text-ink-muted tabular-nums">
          {formatBytes(item.byteCount)}
        </span>
      </span>
      <Icon name="chevron-right" size="1rem" className="text-ink-subtle" />
    </button>
  );
}
